import React from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import {Formik, Form, useFormikContext} from 'formik';
import {Persist} from '../../utils/formik-persist';
import {FormattedMessage, useIntl} from 'react-intl';
import cities from 'wobi-web-common/dist/config/cities';
import {validateForStreetHebrew, validateForEnglish, validatePhoneNumber, validateEmail} from '../../utils/validationHelpers';
import {
  getCityStreets,
  handleAdditionalDetailsSubmit,
} from '../../utils/apiHandlers';
import {getDetailForm, getOffersFields, preSaveHandler, setDetailForm} from '../../utils/persistOfferDetails';
import {fireGtmEvent} from '../../utils/marketing.utils';
import ErrorMessagePopup from '../../components/modals/ErrorMessage';
import {getAge} from '../../utils/dateHelpers';
import {removeGershaim} from '../../utils/streets';
import {buildCityOptions, buildStreetOptions} from '../../utils/addressAutocomplete';
import {scrollToFirstError} from '../../utils/scrollToFirstError';
import {Autocomplete, FormField, Input, Note} from 'wobi-web-common/dist/v2/index.js';
import CompleteDetailsLayout from '../../components/CompleteDetailsLayout';
import InsuredIssueCard from '../../components/InsuredIssueCard';
import './CompleteDetails.css';

const firstNameValidationText = 'נא להזין שם פרטי באנגלית';
const lastNameValidationText = 'נא להזין שם משפחה באנגלית';
const streetValidationText = 'נא להזין שם רחוב בעברית';

// English-name fields accept only Latin letters, spaces and hyphens; anything else (e.g. Hebrew) is dropped on input.
const stripNonEnglishChars = value => String(value || '').replace(/[^A-Za-z\s-]/g, '');

const CityStreetSync = ({cityCode, onCityCodeChange}) => {
  const {values} = useFormikContext();

  React.useEffect(() => {
    if (values.city_c && values.city_c !== cityCode) {
      onCityCodeChange(values.city_c);
    }
  }, [cityCode, onCityCodeChange, values.city_c]);

  return null;
};

CityStreetSync.propTypes = {
  cityCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCityCodeChange: PropTypes.func.isRequired,
};

const CompleteDetails = React.memo(({className, offer, initialTabs, onBack, submitAct}) => {
  const intl = useIntl();
  const messages = (id) => intl.formatMessage({id});
  const [cityCode, setCityCode] = React.useState(null);
  const [streets, setStreets] = React.useState([]);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = React.useState(false);
  const [validationObject, setValidationObject] = React.useState({
    city_c: yup.string().typeError().required('שדה חובה'),
    street: validateForStreetHebrew(streetValidationText),
    buildingNumber: yup.number().typeError('יש להזין מספר').required('שדה חובה'),
    firstNameEng: validateForEnglish(firstNameValidationText),
    lastNameEng: validateForEnglish(lastNameValidationText),
    email: validateEmail(),
  });

  const isProviderClal = () => offer.provider === 'CLAL';
  const isAgeOver18 = dob => getAge(dob) >= 18;
  const isHidePermementlyClalPaymentFields = true;
  const showClalPaymentFields = dob => !isHidePermementlyClalPaymentFields && isAgeOver18(dob) && isProviderClal();
  const isAdvanceToCreditCardEnabled = true;
  const showClalAandvceToCreditCardField = isAdvanceToCreditCardEnabled && isProviderClal();

  React.useEffect(() => {
    if (cityCode > 0) {
      getCityStreets(cityCode)
        .then(string => {
          setStreets(string.data);
          return string.data;
        })
        .catch(() => {
          setStreets([]);
        });
    }
  }, [cityCode]);

  React.useEffect(() => {
    const newValidation = {...validationObject};
    if (initialTabs.policyholdNum > 1) {
      const countArray = Array.from(new Array(initialTabs.policyholdNum + 1).keys()).slice(2);
      Object.keys(countArray).forEach(indx => {
        newValidation['firstNameEng' + countArray[indx]] = validateForEnglish(firstNameValidationText);
        newValidation['lastNameEng' + countArray[indx]] = validateForEnglish(lastNameValidationText);

        // newValidation['ID' + (Number(indx) + 2)] = validateForIsraeliID();
      });

      // const ids = Array
      //   .from({length: initialTabs.policyholdNum - 1}, (_, n) => n + 2);

      // ids.forEach(i => {
      //   newValidation[`ID${i}`] =
      //   validateForIsraeliID()
      //     .test(
      //       'unique-id',
      //       'מספר זה כבר קיים',
      //       function (value) {
      //       // collect all the sibling IDs
      //         const allIds = ids.map(j => this.parent[`ID${j}`]);
      //         allIds.push(getID1());

      //         // count how many times this value appears
      //         const occurrences = allIds.filter(x => x === value).length;
      //         return !value || occurrences === 1;
      //       },
      //     );
      // });
      setValidationObject(newValidation);
    }

    if (isProviderClal()) {
      const savePaymentInfoErrorMessage = 'יש לאשר את תנאי השימוש';

      if (isAgeOver18(initialTabs.dob1) && !showClalAandvceToCreditCardField) {
        newValidation.payWithDigitalCard = yup.string().required('שדה חובה');
        newValidation.savePaymentInfo = yup.boolean().when('payWithDigitalCard', {
          is: 'yes',
          then: yup.boolean().oneOf([true], savePaymentInfoErrorMessage).required(savePaymentInfoErrorMessage),
        });
      }

      if (initialTabs.travelers.length > 1) {
        Array.from(new Array(initialTabs.policyholdNum + 1).keys()).slice(2)
          .forEach((index) => {
            if (isAgeOver18(initialTabs[`dob${index}`]) && !showClalAandvceToCreditCardField) {
              newValidation[`payWithDigitalCard${index}`] = yup.string().required('שדה חובה');
              newValidation[`additionalInsuredEmail${index}`] = yup.string().when(`payWithDigitalCard${index}`, {
                is: 'yes',
                then: validateEmail(),
              });
              newValidation[`additionalInsuredPhone${index}`] = yup.string().when(`payWithDigitalCard${index}`, {
                is: 'yes',
                then: validatePhoneNumber(),
              });
              newValidation[`savePaymentInfo${index}`] = yup.boolean().when(`payWithDigitalCard${index}`, {
                is: 'yes',
                then: yup.boolean().oneOf([true], savePaymentInfoErrorMessage).required(savePaymentInfoErrorMessage),
              });
            }
          });
      }

      setValidationObject(newValidation);
    }
  }, [initialTabs]);

  const handleCityInputChange = async (query, values, setFieldValue) => {
    if (values.city_c) await setFieldValue('city_c', '', false);
    await setFieldValue('city_name', query, false);
  };

  const handleCitySelect = async (event, setFieldValue) => {
    await setFieldValue('city_name', event ? event.name : '', false);
    await setFieldValue('city_c', event ? event.value : '');
    if (event && event.value !== cityCode) {
      setCityCode(event.value);
    }
  };

  const handleSubmit = async (values, validateForm, setFieldTouched) => {
    setSubmitting(true);
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      const normalizedStreet = removeGershaim(values.street);
      const normalizedValues = {
        ...values,
        street: normalizedStreet,
      };
      const detailsForm = getDetailForm('values');

      // detailsForm.travelers.forEach((traveler, index) => {
      //   const travelerIdSTR = 'ID' + (index + 1);
      //   if (values[travelerIdSTR]) {
      //     traveler.ID = values[travelerIdSTR];
      //     detailsForm[travelerIdSTR] = values[travelerIdSTR];
      //   }
      // });
      setDetailForm({values: detailsForm});

      fireGtmEvent('confirmTravelAdditionalDetails');
      const resp = await handleAdditionalDetailsSubmit({...normalizedValues,
        offersCallId: getOffersFields('offersCallId'),
      });

      if (resp && resp.success) {
        setSubmitting(false);
        preSaveHandler();
        sessionStorage.setItem('travel.complete-details', JSON.stringify({values: normalizedValues}));
        submitAct();
      } else {
        setSubmitting(false);
        setErrorModalOpen(true);
      }
    } else {
      Object.keys(validationObject).forEach(field => setFieldTouched(field, true));
      setSubmitting(false);
      scrollToFirstError(errors);
    }
  };

  return (
    <div className={className} data-testid='complete-details-container'>
      <Formik
        validationSchema={yup.object().shape(validationObject)}
        initialValues={{
          city_c: '',
          city_name: '',
          street: '',
          buildingNumber: '',
          firstNameEng: '',
          lastNameEng: '',
          payWithDigitalCard: '',
          savePaymentInfo: '',
          email: '',
        }}>
        {({values, errors, touched, setFieldValue, validateForm, setFieldTouched}) => {
          function fieldError(name) {
            return touched[name] ? errors[name] : undefined;
          }
          const cityOptions = buildCityOptions(cities, values.city_name || '');
          const streetOptions = buildStreetOptions(streets, values.street || '');
          const additionalInsureds = Array.from(
            {length: Math.max(0, initialTabs.policyholdNum - 1)},
            (_, index) => index + 2,
          );

          return (
            <Form>
              <CityStreetSync cityCode={cityCode} onCityCodeChange={setCityCode}/>
              <CompleteDetailsLayout
                isSubmitting={isSubmitting}
                onBack={onBack}
                onSubmit={() => handleSubmit(values, validateForm, setFieldTouched)}
              >
              <InsuredIssueCard
                primary
                title={messages(`step3.mTraveler${initialTabs.gender1 === '2' ? '-f' : ''}`)}
                identity={{
                  name: `${initialTabs.firstName1 || ''} ${initialTabs.lastName1 || ''}`.trim(),
                  idNumber: initialTabs.ID1,
                  dateOfBirth: initialTabs.dob1,
                  phone: initialTabs.phone1,
                }}
              >
                <div className='insured-issue-card__fields' data-testid='complete-details-primary-fields'>
                  <Note variant='notice' style={{gridColumn: '1 / -1'}}>
                    <FormattedMessage id='completeDetails.primaryNotice'/>
                  </Note>
                  <FormField
                    label={messages('step3.firstname_eng')}
                    htmlFor='firstNameEng'
                    error={fieldError('firstNameEng')}
                  >
                    <Input
                      id='firstNameEng'
                      size='lg'
                      autoFocus
                      value={values.firstNameEng || ''}
                      onChange={event => setFieldValue('firstNameEng', stripNonEnglishChars(event.target.value))}
                      onBlur={() => setFieldTouched('firstNameEng', true)}
                      invalid={Boolean(fieldError('firstNameEng'))}
                      inputStyle={{direction: 'ltr', textAlign: 'left'}}
                      placeholder='First name'
                      data-testid='first-name-1'
                    />
                  </FormField>
                  <FormField
                    label={messages('step3.lastname_eng')}
                    htmlFor='lastNameEng'
                    error={fieldError('lastNameEng')}
                  >
                    <Input
                      id='lastNameEng'
                      size='lg'
                      value={values.lastNameEng || ''}
                      onChange={event => setFieldValue('lastNameEng', stripNonEnglishChars(event.target.value))}
                      onBlur={() => setFieldTouched('lastNameEng', true)}
                      invalid={Boolean(fieldError('lastNameEng'))}
                      inputStyle={{direction: 'ltr', textAlign: 'left'}}
                      placeholder='Last name'
                      data-testid='last-name-1'
                    />
                  </FormField>
                  <FormField
                    label={messages('step3.email')}
                    htmlFor='complete-details-email'
                    error={fieldError('email')}
                  >
                    <Input
                      id='complete-details-email'
                      size='lg'
                      type='email'
                      value={values.email || ''}
                      onChange={event => setFieldValue('email', event.target.value)}
                      onBlur={() => setFieldTouched('email', true)}
                      invalid={Boolean(fieldError('email'))}
                      inputStyle={{direction: 'ltr', textAlign: 'left'}}
                      placeholder='name@email.com'
                      data-testid='complete-details-email'
                    />
                  </FormField>
                  <FormField label={messages('completeDetails.city')} htmlFor='city_c' error={fieldError('city_c')}>
                    <div data-testid='complete-details-city'>
                      <Autocomplete
                        id='city_c'
                        name='city_c'
                        size='lg'
                        value={values.city_c ? {value: values.city_c, label: values.city_name} : null}
                        inputValue={values.city_name || ''}
                        options={cityOptions}
                        onInputChange={query => handleCityInputChange(query, values, setFieldValue)}
                        onChange={option => handleCitySelect(option, setFieldValue)}
                        onBlur={() => {
                          if (!values.city_c) {
                            setFieldValue('city_name', '');
                          }
                          setFieldTouched('city_c', true);
                        }}
                        placeholder={messages('step3.select_city')}
                        invalid={Boolean(fieldError('city_c'))}
                        showOptionsWhenEmpty={false}
                      />
                    </div>
                  </FormField>
                  <FormField label={messages('completeDetails.street')} htmlFor='street' error={fieldError('street')}>
                    <div data-testid='complete-details-street'>
                      <Autocomplete
                        id='street'
                        name='street'
                        size='lg'
                        value={values.street ? {value: values.street, label: values.street} : null}
                        inputValue={values.street || ''}
                        options={streetOptions}
                        onInputChange={query => setFieldValue('street', query)}
                        onChange={option => setFieldValue(
                          'street',
                          option ? removeGershaim(option.value || option.label || '') : '',
                        )}
                        onBlur={() => setFieldTouched('street', true)}
                        placeholder={messages('step3.select_street')}
                        invalid={Boolean(fieldError('street'))}
                        allowCustomValue
                        showOptionsWhenEmpty={false}
                      />
                    </div>
                  </FormField>
                  <FormField label={messages('step3.building_number')} htmlFor='buildingNumber' error={fieldError('buildingNumber')}>
                    <Input
                      id='buildingNumber'
                      size='lg'
                      inputMode='numeric'
                      value={values.buildingNumber || ''}
                      onChange={event => setFieldValue('buildingNumber', event.target.value)}
                      onBlur={() => setFieldTouched('buildingNumber', true)}
                      invalid={Boolean(fieldError('buildingNumber'))}
                      data-testid='complete-details-building-number'
                    />
                  </FormField>
                </div>
                {showClalAandvceToCreditCardField ? (
                  <p className='insured-issue-card__identity'>
                    <FormattedMessage id='step3.advanceToCreditCard' values={{br: <br/>}}/>
                  </p>
                ) : null}
              </InsuredIssueCard>

              {additionalInsureds.map(index => (
                <InsuredIssueCard
                  key={index}
                  title={`${messages(`step3.traveler${initialTabs[`gender${index}`] === '2' ? '-f' : ''}`)} ${index}`}
                  identity={{
                    name: `${initialTabs[`firstName${index}`] || ''} ${initialTabs[`lastName${index}`] || ''}`.trim(),
                    idNumber: initialTabs[`ID${index}`],
                    dateOfBirth: initialTabs[`dob${index}`],
                  }}
                >
                  <div className='insured-issue-card__fields'>
                    <FormField
                      label={messages('step3.firstname_eng')}
                      htmlFor={`firstNameEng${index}`}
                      error={fieldError(`firstNameEng${index}`)}
                    >
                      <Input
                        id={`firstNameEng${index}`}
                        size='lg'
                        value={values[`firstNameEng${index}`] || ''}
                        onChange={event => setFieldValue(`firstNameEng${index}`, stripNonEnglishChars(event.target.value))}
                        onBlur={() => setFieldTouched(`firstNameEng${index}`, true)}
                        invalid={Boolean(fieldError(`firstNameEng${index}`))}
                        inputStyle={{direction: 'ltr', textAlign: 'left'}}
                        placeholder='First name'
                        data-testid={`first-name-${index}`}
                      />
                    </FormField>
                    <FormField
                      label={messages('step3.lastname_eng')}
                      htmlFor={`lastNameEng${index}`}
                      error={fieldError(`lastNameEng${index}`)}
                    >
                      <Input
                        id={`lastNameEng${index}`}
                        size='lg'
                        value={values[`lastNameEng${index}`] || ''}
                        onChange={event => setFieldValue(`lastNameEng${index}`, stripNonEnglishChars(event.target.value))}
                        onBlur={() => setFieldTouched(`lastNameEng${index}`, true)}
                        invalid={Boolean(fieldError(`lastNameEng${index}`))}
                        inputStyle={{direction: 'ltr', textAlign: 'left'}}
                        placeholder='Last name'
                        data-testid={`last-name-${index}`}
                      />
                    </FormField>
                  </div>
                </InsuredIssueCard>
              ))}
              </CompleteDetailsLayout>
              <ErrorMessagePopup onClose={() => setErrorModalOpen(false)} isOpen={isErrorModalOpen}/>
              <Persist name='travel.complete-details' isSessionStorage='true' preSaveHandler={preSaveHandler}/>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

CompleteDetails.propTypes = {
  className: PropTypes.string,
  offer: PropTypes.object.isRequired,
  initialTabs: PropTypes.shape({
    gender1: PropTypes.string,
    firstName1: PropTypes.string,
    lastName1: PropTypes.string,
    ID1: PropTypes.string,
    dob1: PropTypes.string,
    policyholdNum: PropTypes.number,
    travelers: PropTypes.array,
  }),
  onBack: PropTypes.func.isRequired,
  submitAct: PropTypes.func,
};

export default CompleteDetails;
